/**
 * dubbingPollAdapter - Normalizes dubbing job status into PollUpdate consumed
 * by GenerationContext.
 *
 * Both video and audio dubbing share the same status endpoint
 * (/api/v1/jobs/{id}) and the same dubbing store on the backend
 * (serverType: 'dubbing'). The kind discriminator stored in job
 * metadata is only used by the rendering layer to branch the result UI.
 *
 * Self-registers on module load so GenerationContext can resume polling
 * persisted dubbing jobs after a reload.
 */

import { DubbingAPI } from '../dubbing';
import type { DubbingResponse } from '../../types';
import type { AudioDubbingResponse } from '../dubbing';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

/** Discriminates between the two dubbing flows when rendering the result. */
export type DubbingKind = 'video' | 'audio';

/** Unified result stored in GenerationJob.result for both dubbing kinds. */
export interface DubbingJobResult {
  kind: DubbingKind;
  /** URL serving the artifact, whichever kind it is. */
  downloadUrl: string | null;
  /** Absolute local path of the artifact (desktop builds only). */
  localFilePath: string | null;
  /** Populated when kind === 'video' */
  dubbingResult?: DubbingResponse;
  /** Populated when kind === 'audio' */
  audioDubbingResult?: AudioDubbingResponse;
}

export const dubbingPollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<DubbingJobResult>> => {
  const statusResponse = await DubbingAPI.getDubbingStatus(jobId);

  if (!statusResponse.success) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: statusResponse.error ?? 'Failed to get dubbing status',
        sanitized: sanitizeBackendError(statusResponse),
      },
    };
  }

  const {
    stage,
    progress,
    message,
    data,
    started_at,
    elapsed_seconds,
    estimated_remaining_seconds,
  } = statusResponse;

  const timing = {
    startedAt: started_at ?? null,
    elapsedSeconds: elapsed_seconds ?? null,
    estimatedRemainingSeconds: estimated_remaining_seconds ?? null,
  };

  // Terminal: error
  if (stage === 'error' || statusResponse.error) {
    return {
      done: true,
      status: 'failed',
      error: {
        message: statusResponse.error ?? message ?? 'Dubbing failed',
        sanitized: sanitizeBackendError(statusResponse),
      },
      timing,
    };
  }

  // Terminal: completed
  if (stage === 'completed' && data?.success) {
    const completed = data as DubbingResponse;
    const downloadUrl = completed.download_url ?? null;
    const localFilePath = completed.output_file_path ?? null;

    // The kind comes from the backend, which knows which pipeline ran. Every
    // producer now reports it: the desktop task store, the Cloud Run handlers
    // and the RunPod workers. It used to be inferred here as
    // `download_url && !output_video_url` — but the backend populated both
    // keys from the same string, so the audio branch was unreachable and
    // every dubbed MP3 was rendered to the user as a dubbed video.
    const isAudio = completed.media_kind === 'audio';

    const result: DubbingJobResult = isAudio
      ? {
          kind: 'audio',
          downloadUrl,
          localFilePath,
          audioDubbingResult: {
            success: true,
            job_id: jobId,
            download_url: downloadUrl,
            detected_language: completed.detected_language ?? 'unknown',
            audio_tracks: (completed.audio_tracks as Record<string, number> | undefined) ?? {},
            languages_processed: completed.languages_processed ?? [],
            speed_factors: completed.speed_factors ?? null,
            processing_time_seconds: completed.processing_time_seconds ?? 0,
            error: null,
          },
        }
      : {
          kind: 'video',
          downloadUrl,
          localFilePath,
          dubbingResult: completed,
        };

    return {
      done: true,
      status: 'completed',
      progress: 100,
      stage: 'completed',
      result,
      timing,
    };
  }

  // Non-terminal: still processing
  // Ignore 'pending' stage to avoid overwriting the initial UI message.
  if (!stage || stage === 'pending') {
    return {
      done: false,
      status: 'running',
      timing,
    };
  }

  return {
    done: false,
    status: 'running',
    progress: typeof progress === 'number' ? progress : undefined,
    stage,
    message: message ?? undefined,
    timing,
  };
};

registerPollAdapter('dubbing', dubbingPollAdapter);
